import { AxiosResponse } from 'axios';
import type {
  IETLConfigurationsResponse,
  IETLCompleteSetupRequest,
  IETLCompleteSetupResponse,
  IETLStatusResponse,
} from '../../interface/etlDashboard.interface';
import type {
  ICurriculumTemplatesResponse,
  ICurrentQuickSetupStatusResponse,
  IExecuteQuickSetupResponse,
  IQuickSetupStatusResponse,
} from '../../interface/etlQuickSetup.interface';
import api from '../api';

/**
 * Fetch all ETL configurations for the organization
 * GET /api/v2/etl/configurations
 */
const fetchETLConfigurations = async (): Promise<AxiosResponse<IETLConfigurationsResponse>> => {
  const response: AxiosResponse<IETLConfigurationsResponse> = await api.get('/etl/configurations');
  return response;
};

/**
 * Complete ETL setup
 * PATCH /api/v2/etl/complete
 * No longer returns tokens — Super Admin already has full privileges
 */
const completeETLSetup = async (
  payload: IETLCompleteSetupRequest,
): Promise<AxiosResponse<IETLCompleteSetupResponse>> => {
  const response: AxiosResponse<IETLCompleteSetupResponse> = await api.patch(
    '/etl/complete',
    payload,
  );
  return response;
};

/**
 * Fetch ETL status (setup completion + one-time paywall banner flag)
 * GET /api/v2/etl/status
 * NOTE: Server writes IS_VIEW_ETL_PAYWALL_BANNER preference atomically on first call.
 */
const getETLStatus = async (): Promise<AxiosResponse<IETLStatusResponse>> => {
  const response: AxiosResponse<IETLStatusResponse> = await api.get('/etl/status');
  return response;
};

/**
 * Fetch curriculum/program templates for Quick Setup flow
 * GET /api/v2/etl/quick-setup/templates
 */
const fetchCurriculumTemplates = async (): Promise<AxiosResponse<ICurriculumTemplatesResponse>> => {
  const response: AxiosResponse<ICurriculumTemplatesResponse> = await api.get(
    '/etl/quick-setup/templates',
  );
  return response;
};

/**
 * Trigger asynchronous Quick Setup execution — CVA-3417
 * POST /api/v2/etl/quick-setup/execute
 * Returns HTTP 202 with a jobId for status polling
 */
const executeQuickSetup = async (
  templateId: string,
): Promise<AxiosResponse<IExecuteQuickSetupResponse>> => {
  const response: AxiosResponse<IExecuteQuickSetupResponse> = await api.post(
    '/etl/quick-setup/execute',
    { templateId },
  );
  return response;
};

/**
 * Poll execution status of a Quick Setup job — CVA-3417
 * GET /api/v2/etl/quick-setup/execute/status?jobId=xxx
 */
const getQuickSetupStatus = async (
  jobId: string,
): Promise<AxiosResponse<IQuickSetupStatusResponse>> => {
  const response: AxiosResponse<IQuickSetupStatusResponse> = await api.get(
    '/etl/quick-setup/execute/status',
    { params: { jobId } },
  );
  return response;
};

/**
 * Fetch latest Quick Setup job for the current organization — refresh/session recovery
 * GET /api/v2/etl/quick-setup/status
 */
const getCurrentQuickSetupStatus = async (
  templateId?: string,
): Promise<AxiosResponse<ICurrentQuickSetupStatusResponse>> => {
  const response: AxiosResponse<ICurrentQuickSetupStatusResponse> = await api.get(
    '/etl/quick-setup/status',
    { params: { templateId } },
  );
  return response;
};

/**
 * Export all ETL service functions as a grouped object
 * MANDATORY PATTERN: Follow curriculum service structure
 * NOTE: Bulk upload jobs are fetched via messageQueueService.fetchJobs() instead
 */
export const etlService = {
  fetchETLConfigurations,
  completeETLSetup,
  getETLStatus,
  fetchCurriculumTemplates,
  executeQuickSetup,
  getQuickSetupStatus,
  getCurrentQuickSetupStatus,
};
